import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Phone, MapPin, Bed, Bath, Maximize, Toilet, ArrowRight, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react'
import { motion } from 'framer-motion'
import ChatWidget from '@/components/ChatWidget'
import PropertyFilters, { FILTERS_INITIAL_STATE, PropertyFiltersState, applyFilters } from '@/components/PropertyFilters'
import PropertyPagination from '@/components/PropertyPagination'

interface Property {
  id: string
  seo_title: string
  title: string
  description: string
  price: number
  currency: string
  property_type: string
  purpose: string
  bedrooms?: number
  bathrooms?: number
  half_bathrooms?: number
  area_m2?: number
  zone: string
  city?: string
  amenities?: string[]
  featured_image?: string
}

interface MinimalistaTemplateProps {
  agent: {
    name: string
    bio: string
    phone: string
    specialties: string[]
    zones: string[]
    avatar_url?: string
    social_media?: {
      facebook?: string
      instagram?: string
      linkedin?: string
      tiktok?: string
      youtube?: string
    }
  }
}

const MinimalistaTemplate: React.FC<MinimalistaTemplateProps> = ({ agent }) => {
  const navigate = useNavigate()
  const [properties, setProperties] = useState<Property[]>([])
  const [filters, setFilters] = useState<PropertyFiltersState>(FILTERS_INITIAL_STATE)
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(6)

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const subdomain = window.location.hostname.split('.')[0]
        const response = await fetch(`/api/agents/subdomain/${subdomain}/properties`)
        if (response.ok) {
          const data = await response.json()
          setProperties(data)
        }
      } catch (error) {
        console.error('Error fetching properties:', error)
      }
    }
    fetchProperties()
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [filters])

  const formatPrice = (price: number, currency: string) => {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currency}`
  }

  const handlePropertyClick = (seoTitle: string) => {
    navigate(`/property/${seoTitle}`)
  }

  const filteredProperties = applyFilters(properties, filters)
  const propertyTypes = [...new Set(properties.map(p => p.property_type).filter(Boolean))]
  const totalPages = perPage === 0 ? 1 : Math.ceil(filteredProperties.length / perPage)
  const paginatedProperties = perPage === 0
    ? filteredProperties
    : filteredProperties.slice((currentPage - 1) * perPage, currentPage * perPage)
  const showPhone = false
  const showWhatsapp = false

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>

      {/* Nav minimalista */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-8 py-5 flex items-center justify-between">
          <span className="text-sm tracking-[0.3em] uppercase text-gray-400 font-light">
            {agent.name}
          </span>
          {agent.phone && (
            <a href={`tel:${agent.phone}`}
              className="text-sm tracking-widest uppercase text-gray-900 hover:text-gray-500 transition-colors">
              Contacto
            </a>
          )}
        </div>
      </header>

      {/* Hero — tipografía protagonista */}
      <section className="min-h-screen flex items-center pt-20">
        <div className="max-w-6xl mx-auto px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-24 items-center min-h-[80vh]">

            {/* Texto */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="space-y-10"
            >
              <div className="space-y-2">
                <p className="text-xs tracking-[0.4em] uppercase text-gray-400">Asesor Inmobiliario</p>
                <h1 className="text-6xl lg:text-8xl font-light text-gray-900 leading-none tracking-tight">
                  {agent.name.split(' ').map((word, i) => (
                    <span key={i} className="block">{word}</span>
                  ))}
                </h1>
              </div>

              {agent.bio && (
                <p className="text-lg text-gray-500 leading-relaxed max-w-md font-light">
                  {agent.bio}
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                {(showPhone || showWhatsapp) && agent.phone && (
                  <>
                    {showPhone && (
                      <a href={`tel:${agent.phone}`}
                        className="group flex items-center gap-3 px-8 py-4 bg-gray-900 text-white text-sm tracking-widest uppercase hover:bg-gray-700 transition-colors duration-300">
                        <Phone size={16} />
                        {agent.phone}
                        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                      </a>
                    )}
                    {showWhatsapp && (
                      <a href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, '')}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 px-8 py-4 bg-[#25D366] text-white text-sm tracking-widest uppercase hover:bg-[#1fb855] transition-colors duration-300">
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        WhatsApp
                      </a>
                    )}
                  </>
                )}
              </div>0

              {/* Social media — minimalista */}
              {agent.social_media && Object.values(agent.social_media).some(v => v) && (
                <div className="flex gap-6 pt-2">
                  {agent.social_media.facebook && (
                    <a href={agent.social_media.facebook} target="_blank" rel="noopener noreferrer"
                      className="text-gray-300 hover:text-gray-900 transition-colors">
                      <Facebook size={18} />
                    </a>
                  )}
                  {agent.social_media.instagram && (
                    <a href={agent.social_media.instagram} target="_blank" rel="noopener noreferrer"
                      className="text-gray-300 hover:text-gray-900 transition-colors">
                      <Instagram size={18} />
                    </a>
                  )}
                  {agent.social_media.linkedin && (
                    <a href={agent.social_media.linkedin} target="_blank" rel="noopener noreferrer"
                      className="text-gray-300 hover:text-gray-900 transition-colors">
                      <Linkedin size={18} />
                    </a>
                  )}
                  {agent.social_media.tiktok && (
                    <a href={agent.social_media.tiktok} target="_blank" rel="noopener noreferrer"
                      className="text-gray-300 hover:text-gray-900 transition-colors">
                      <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                      </svg>
                    </a>
                  )}
                  {agent.social_media.youtube && (
                    <a href={agent.social_media.youtube} target="_blank" rel="noopener noreferrer"
                      className="text-gray-300 hover:text-gray-900 transition-colors">
                      <Youtube size={18} />
                    </a>
                  )}
                </div>
              )}
            </motion.div>

            {/* Avatar — encuadre editorial */}
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              {agent.avatar_url ? (
                <div className="relative">
                  <img
                    src={agent.avatar_url}
                    alt={agent.name}
                    className="w-full aspect-[3/4] object-cover grayscale hover:grayscale-0 transition-all duration-700"
                  />
                  <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-white to-transparent" />
                  <div className="absolute bottom-8 left-8">
                    <p className="text-xs tracking-[0.3em] uppercase text-gray-500">
                      {properties.length} propiedades
                    </p>
                  </div>
                </div>
              ) : (
                <div className="w-full aspect-[3/4] bg-gray-100 flex items-center justify-center">
                  <span className="text-9xl font-light text-gray-200">{agent.name.charAt(0)}</span>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Línea divisora */}
      <div className="max-w-6xl mx-auto px-8">
        <div className="h-px bg-gray-100" />
      </div>

      {/* Propiedades */}
      {properties.length > 0 && (
        <section className="py-24">
          <div className="max-w-6xl mx-auto px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="mb-4"
            >
              <div className="flex items-baseline justify-between mb-2">
                <h2 className="text-xs tracking-[0.4em] uppercase text-gray-400">Portafolio</h2>
                <span className="text-xs text-gray-300">{properties.length} propiedades</span>
              </div>
              <h3 className="text-4xl lg:text-5xl font-light text-gray-900">Selección</h3>

              <PropertyFilters
                filters={filters}
                onFiltersChange={setFilters}
                propertyTypes={propertyTypes}
                totalProperties={properties.length}
                filteredCount={filteredProperties.length}
                colorSchemeKey="navy"
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(v => !v)}
              />
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-1 mt-10">
              {paginatedProperties.map((property, index) => (
                <motion.article
                  key={property.id}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: index * 0.08, duration: 0.6 }}
                  viewport={{ once: true }}
                  onClick={() => handlePropertyClick(property.seo_title)}
                  className="group cursor-pointer"
                >
                  {/* Imagen */}
                  <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                    {property.featured_image ? (
                      <img
                        src={property.featured_image}
                        alt={property.title}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <span className="text-gray-300 text-4xl font-light">{index + 1}</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
                  </div>

                  {/* Info */}
                  <div className="p-6 border border-t-0 border-gray-100">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-xs tracking-widest uppercase text-gray-400 mb-1">{property.purpose} · {property.property_type}</p>
                        <h3 className="text-lg font-light text-gray-900 group-hover:text-gray-500 transition-colors">
                          {property.title}
                        </h3>
                      </div>
                      <ArrowRight size={16} className="text-gray-300 group-hover:text-gray-900 group-hover:translate-x-1 transition-all mt-1 flex-shrink-0" />
                    </div>
                    <p className="text-sm text-gray-400 flex items-center gap-1 mb-3">
                      <MapPin size={12} /> {property.zone}{property.city ? `, ${property.city}` : ''}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        {property.bedrooms && <span className="flex items-center gap-1"><Bed size={12} /> {property.bedrooms}</span>}
                        {property.bathrooms && <span className="flex items-center gap-1"><Bath size={12} /> {property.bathrooms}</span>}
                        {property.half_bathrooms > 0 && <span className="flex items-center gap-1"><Toilet size={12} /> {property.half_bathrooms}</span>}
                        {property.area_m2 && <span className="flex items-center gap-1"><Maximize size={12} /> {property.area_m2}m²</span>}
                      </div>
                      <span className="text-sm font-light text-gray-900">
                        {formatPrice(property.price, property.currency)}
                      </span>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>

            <PropertyPagination
              currentPage={currentPage}
              totalPages={totalPages}
              perPage={perPage}
              onPageChange={setCurrentPage}
              onPerPageChange={setPerPage}
              colorSchemeKey="navy"
            />
          </div>
        </section>
      )}

      {/* Línea divisora */}
      <div className="max-w-6xl mx-auto px-8">
        <div className="h-px bg-gray-100" />
      </div>

      {/* Especialidades — texto puro */}
      {agent.specialties?.length > 0 && (
        <section className="py-24">
          <div className="max-w-6xl mx-auto px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <p className="text-xs tracking-[0.4em] uppercase text-gray-400 mb-2">Expertise</p>
              <h3 className="text-4xl lg:text-5xl font-light text-gray-900 mb-12">Especialidades</h3>
              <div className="grid md:grid-cols-2 gap-x-16 gap-y-4">
                {agent.specialties.map((specialty, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.06 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-4 py-4 border-b border-gray-100"
                  >
                    <span className="text-xs text-gray-300 w-6">{String(index + 1).padStart(2, '0')}</span>
                    <span className="text-gray-700 font-light capitalize">{specialty}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Zonas */}
      {agent.zones?.length > 0 && (
        <section className="py-24 bg-gray-50">
          <div className="max-w-6xl mx-auto px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <p className="text-xs tracking-[0.4em] uppercase text-gray-400 mb-2">Cobertura</p>
              <h3 className="text-4xl lg:text-5xl font-light text-gray-900 mb-12">Zonas</h3>
              <div className="flex flex-wrap gap-3">
                {agent.zones.map((zone, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="px-6 py-3 border border-gray-200 text-gray-600 text-sm font-light hover:border-gray-900 hover:text-gray-900 transition-colors cursor-default"
                  >
                    {zone}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Footer CTA */}
      <section className="py-32 bg-gray-900">
        <div className="max-w-6xl mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="grid lg:grid-cols-2 gap-16 items-end"
          >
            <div className="space-y-6">
              <p className="text-xs tracking-[0.4em] uppercase text-gray-500">Contacto</p>
              <h2 className="text-5xl lg:text-6xl font-light text-white leading-tight">
                Hablemos de tu próxima propiedad.
              </h2>
            </div>
            <div className="space-y-4">
              {agent.phone && (
                <>
                  <a href={`tel:${agent.phone}`}
                    className="flex items-center justify-between w-full py-5 border-b border-gray-700 text-white hover:text-gray-300 transition-colors group">
                    <span className="text-sm tracking-widest uppercase">Llamar</span>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-400">{agent.phone}</span>
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </a>
                  <a href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, '')}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-between w-full py-5 border-b border-gray-700 text-white hover:text-gray-300 transition-colors group">
                    <span className="text-sm tracking-widest uppercase">WhatsApp</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </a>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <ChatWidget
        agentName={agent.name}
        agentSubdomain={window.location.hostname.split('.')[0]}
        agentAvatar={agent.avatar_url}
        agentPlan={agent.plan}
      />
    </div>
  )
}

export default MinimalistaTemplate
